import { styled } from "@compiled/react";
import { Picture } from "@wac-web/components/wpl/image/src/image";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import Link from "@atlaskit/link";
import { Inline, Stack, Text } from "@atlaskit/primitives-next";
import Skeleton from "@atlaskit/skeleton";
import { Card } from "@atlaskit/smart-card";

import { useMigrationsManagementUIAnalytics } from "../hooks";

import { BoardAndEmbedViewOptions } from "./board-and-embed-view-options";
import { messages } from "./messages";
import { useJiraBoardEmbeds } from "./use-jira-board-embeds";

const EmbedContainer = styled.div({
    "width": "100%",
    // this is the recommended approach from smart card package documentation
    // https://atlaskit.atlassian.com/packages/linking-platform/smart-card/docs/embed-card
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& .loader-wrapper": {
        height: "650px",
        width: "100%"
    },
    // padding under loader also needs to be removed to match design
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& .loader-wrapper > div": {
        padding: "0"
    }
});

const SelectedBoardProjectTitle = ({
    projectIcon,
    projectName,
    projectEmbedViewUrl,
    onLinkClick
}: {
    projectIcon?: string;
    projectName?: string;
    projectEmbedViewUrl?: string | null;
    onLinkClick: () => void;
}) => {
    const intl = useIntl();
    // placeholder if no icon is available
    if (!projectIcon) {
        return (
            <Inline alignBlock="center" space="space.100">
                <Skeleton
                    height="20px"
                    width="20px"
                    borderRadius="border.radius"
                />

                <Heading size="small">
                    <Link
                        href={projectEmbedViewUrl || ""}
                        target="_blank"
                        onClick={onLinkClick}
                    >
                        {projectName}
                    </Link>
                </Heading>
            </Inline>
        );
    }

    return (
        <Inline alignBlock="center" space="space.100">
            <Picture
                image={{
                    src: projectIcon,
                    alt: intl.formatMessage(messages.logoAltText, {
                        name: projectName
                    })
                }}
                style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "radius.small"
                }}
            />

            <Heading size="small">
                <Link
                    href={projectEmbedViewUrl || ""}
                    target="_blank"
                    onClick={onLinkClick}
                >
                    {projectName}
                </Link>
            </Heading>
        </Inline>
    );
};

const SelectedBoardView = ({
    selectedEmbedViewUrl
}: {
    selectedEmbedViewUrl: string | null;
}) => {
    return (
        <Stack space="space.100">
            <EmbedContainer>
                <Card
                    appearance="embed"
                    inheritDimensions
                    frameStyle="hide"
                    url={selectedEmbedViewUrl ?? ""}
                />
            </EmbedContainer>
            <Text>
                <FormattedMessage
                    {...messages.havingTroubleSubtext}
                    values={{
                        Link: (msg) => (
                            <Link
                                href={selectedEmbedViewUrl ?? ""}
                                target="_blank"
                            >
                                {msg}
                            </Link>
                        )
                    }}
                />
            </Text>
        </Stack>
    );
};

export const JiraEmbeddedBoardSection = () => {
    const { boards, selectedBoard, setSelectedBoard, selectedEmbedViewUrl } =
        useJiraBoardEmbeds();

    const { sendJiraEmbedLinkClickUIEvent } =
        useMigrationsManagementUIAnalytics();

    if (boards.length === 0) {
        return null;
    }

    return (
        <Stack space="space.100">
            <Inline
                space="space.100"
                alignBlock="center"
                grow="fill"
                spread="space-between"
            >
                {selectedBoard && (
                    <SelectedBoardProjectTitle
                        projectName={selectedBoard.projectName}
                        projectIcon={selectedBoard.projectIcon}
                        projectEmbedViewUrl={selectedEmbedViewUrl}
                        onLinkClick={() => {
                            if (selectedEmbedViewUrl) {
                                sendJiraEmbedLinkClickUIEvent(
                                    selectedEmbedViewUrl,
                                    selectedBoard.selectedView
                                );
                            }
                        }}
                    />
                )}
                <BoardAndEmbedViewOptions
                    boards={boards}
                    selectedBoard={selectedBoard}
                    setSelectedBoard={setSelectedBoard}
                />
            </Inline>
            {selectedEmbedViewUrl && (
                <SelectedBoardView
                    selectedEmbedViewUrl={selectedEmbedViewUrl}
                />
            )}
        </Stack>
    );
};
